import { css } from "@emotion/css";
import React, { Key, useMemo, useRef, useState } from "react";
import { useFilter } from "react-aria";
import {
  ComboBoxProps as AriaComboBoxProps,
  Collection,
  Header,
  Section,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { GhostIconButton, Text } from "@certn/cascadia-react";

import { TagGroup } from "./TagGroup";
import { useRefOffsetWidthResize } from "../../hooks/useRefOffsetWidthResize";
import { _Popover } from "../_assets/_Popover";
import { _Text } from "../_assets/_Text";
import { _Input } from "../inputs/_assets/_Input";
import { _InputWrapper } from "../inputs/_assets/_InputWrapper";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../inputs/_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../inputs/_assets/input-wrapper/FormLabelRAC";
import { SelectItem } from "../inputs/select/SelectItem";
import { _ComboBox } from "../inputs/select/assets/_ComboBox";
import { _ListBox } from "../inputs/select/assets/_ListBox";

/**
 * TagGroupSelect requries an array of tag objects { id: number, value: string }, as well as a list of selectedTags[number]. We use the selectedTags array for both showing which tags have been added to the input field and also to display the remaining tags inside the popover.
 *** Most instances should use TagGroupSelectFormikField directly
 *** Refer to TagGroupSelect.stories.tsx for more a breakdown
 */
export interface TagGroupSelectProps<T extends object>
  extends Omit<AriaComboBoxProps<T>, "children" | "onBlur"> {
  label?: string;
  description?: string;
  placeholder?: string;
  errors?: FieldErrorsProps["children"];
  emptyMessage?: string;
  isLoading?: boolean;
  info?: React.ReactNode;
  testID: string;
  tags: Record<string, any>[];
  handleCreateNewTag?: (tag: Record<string, any>[]) => void;
  selectedTags: Record<string, any>[];
  setSelectedTags: (tags: Record<string, any>[]) => void;
  onBlur?: () => void;
}

enum SECTIONS {
  NEW_TAG = "new-tag",
  TAGS = "tags",
}

export function TagGroupSelect<T extends object>(
  props: TagGroupSelectProps<T>
) {
  const {
    label,
    description,
    placeholder,
    emptyMessage,
    isLoading,
    items,
    testID,
    isDisabled,
    handleCreateNewTag,
    tags,
    selectedTags,
    setSelectedTags,
    onBlur,
    name,
    ...comboBoxProps
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const { inputWidth } = useRefOffsetWidthResize(ref);
  const { t } = useTranslation();
  const { contains } = useFilter({ sensitivity: "base" });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const isInputEmpty = !inputValue.trim();
  const hasSelectedTags = !!selectedTags?.length;

  const inputMatchedTag = useMemo(() => {
    return tags.find(
      (tag) => tag?.value?.toLowerCase() === inputValue?.toLowerCase()
    );
  }, [inputValue, tags]);

  const doesInputAlreadyExistAsTag = Boolean(inputMatchedTag);
  const tagAlreadySelected = selectedTags?.find(
    (tag) => tag?.id === inputMatchedTag?.id
  );

  // omit selected tags from popover
  const tagsNotSelected = useMemo(() => {
    const selectedTagIds = selectedTags.map((tag) => tag?.id);
    return tags?.filter((tag) => !selectedTagIds.includes(tag?.id));
  }, [selectedTags, tags]);

  const filteredItems = useMemo(
    () => tagsNotSelected.filter((tag) => contains(tag.value, inputValue)),
    [tagsNotSelected, inputValue, contains]
  );

  const listBoxItems = useMemo(
    () => [
      {
        name: t("Existing tags").toUpperCase(),
        id: SECTIONS.TAGS,
        children: filteredItems,
      },
      // dynamically add new tag section based on inputValue
      {
        name: t("New tag").toUpperCase(),
        id: SECTIONS.NEW_TAG,
        children: [
          {
            id: 0,
            value: `${inputValue} (${t("New tag")})`,
          },
        ],
      },
    ],
    [filteredItems, inputValue, t]
  );

  const tagGroupTags = useMemo(
    () =>
      selectedTags?.map((selectedTag) =>
        tags.find((tag) => tag?.id === selectedTag?.id)
      ),
    [selectedTags, tags]
  ) as Record<string, any>[];

  const handleRemove = (key: Set<Key>) => {
    const tags = selectedTags.filter(
      (selectedTag) => ![...key].includes(selectedTag?.id)
    );
    setSelectedTags(tags);
    setInputValue("");
  };

  const handleAdd = (key: Key) => {
    const isNewTag = key === 0;
    const tagID = isNewTag ? uuidv4() : key;
    const value = isNewTag
      ? inputValue?.trim()
      : tags?.find((tag) => tag?.id === key)?.value;

    const tag = {
      id: tagID,
      value,
    };

    if (isNewTag) {
      handleCreateNewTag?.([...tags, tag]);
    }

    setSelectedTags([...selectedTags, tag]);
    setInputValue("");
  };

  const renderDynamicTitle = (title: string, id: string) => {
    if (!tagsNotSelected?.length && id === SECTIONS.TAGS) {
      return t("No tags found").toUpperCase();
    }

    if (tagAlreadySelected && id === SECTIONS.NEW_TAG) {
      return t("Tag already selected").toUpperCase();
    }

    return title;
  };

  // pressing delete inside empty input field should delete last tag
  const handleBackspace = (event: React.KeyboardEvent) => {
    if (event.key === "Backspace" && isInputEmpty) {
      setSelectedTags(selectedTags.slice(0, -1));
    }
  };

  return (
    <_ComboBox
      {...comboBoxProps}
      menuTrigger="focus"
      isDisabled={isDisabled}
      data-testid={`${testID}-combobox`}
      onSelectionChange={handleAdd}
      selectedKey={null}
      items={listBoxItems as Iterable<T>}
      onFocusChange={(isFocused) => {
        setIsPopoverOpen(isFocused);
        if (!isFocused) {
          setInputValue("");
        }
      }}
      onOpenChange={(isOpen) => {
        setIsPopoverOpen(isOpen);
      }}
    >
      <FormLabelRAC isRequired={props.isRequired} info={props.info}>
        {props.label}
      </FormLabelRAC>
      <div ref={ref}>
        <_InputWrapper
          datatest-id={`${testID}-input-wrapper`}
          className={styles.inputWrapper}
          emptyMessage={emptyMessage}
        >
          {!hasSelectedTags && (
            <GhostIconButton
              // RAC shifts popover due to padding in InputWrapper, this Button only
              // exists until Tags are selected so this keeps the popover from moving up/down
              className={css({ margin: "-8px 0px -8px -8px" })}
              icon={isInputFocused ? "search" : "expand_more"}
            />
          )}
          {hasSelectedTags && (
            <TagGroup
              testID={`${testID}-tag-group`}
              tags={tagGroupTags}
              handleRemove={handleRemove}
              isDisabled={isDisabled}
            />
          )}
          <_Input
            placeholder={
              selectedTags?.length === 0
                ? placeholder ?? t("Search tags")
                : undefined
            }
            name={name}
            value={inputValue}
            onChange={(e) => {
              const value = e?.target?.value;
              // only letters, numbers, underscores and spaces
              setInputValue(value.replace(/[^\w\s]/g, ""));
            }}
            isLoading={isLoading}
            data-testid={`${testID}-input`}
            className={styles.input}
            onKeyDown={handleBackspace}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => {
              setIsInputFocused(false);
              onBlur?.();
            }}
          />
        </_InputWrapper>
      </div>
      {description && (
        <_Text style="input/helper" color="text-weak" slot="description">
          {description}
        </_Text>
      )}
      <FieldErrors>{props.errors}</FieldErrors>
      <_Popover
        triggerRef={ref}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        isLoading={isLoading}
        className={css({ width: `${inputWidth}px` })}
      >
        <_ListBox className={styles.listBox}>
          {(section: Record<string, any>) => {
            const isNewTagSection = section?.id === SECTIONS.NEW_TAG;
            const isTagsSection = section?.id === SECTIONS.TAGS;

            const isExistingTagFilterEmpty =
              !filteredItems.length && !isInputEmpty;

            const isNewTagMatchingOrEmpty =
              (doesInputAlreadyExistAsTag && !tagAlreadySelected) ||
              isInputEmpty;

            return (
              <Section id={section.name}>
                {(isNewTagSection && isNewTagMatchingOrEmpty) ||
                (isTagsSection && isExistingTagFilterEmpty) ? undefined : (
                  <>
                    <Header className={styles.header}>
                      <Text color="text-weak" style="input/section-title">
                        {renderDynamicTitle(section.name, section.id)}
                      </Text>
                    </Header>
                    {/* if tag matches existing tag hide New Tag section */}
                    {isNewTagSection &&
                    doesInputAlreadyExistAsTag ? undefined : (
                      <Collection items={section.children}>
                        {(tag: Record<string, any>) => (
                          <SelectItem
                            className={styles.selectItem}
                            id={tag?.id}
                          >
                            {tag?.value}
                          </SelectItem>
                        )}
                      </Collection>
                    )}
                  </>
                )}
              </Section>
            );
          }}
        </_ListBox>
      </_Popover>
    </_ComboBox>
  );
}

const styles = {
  inputWrapper: css({
    display: "flex",
    flexWrap: "wrap",
    padding: "var(--space-2)",
  }),
  selectItem: css({
    cursor: "pointer",
    height: "var(--space-5)",
    padding: "var(--space-2)",
    margin: "var(--space-1)",
  }),
  header: css({
    padding: "var(--space-2) var(--space-3)",
    display: "flex",
  }),
  listBox: css({ gap: "0px" }),
  input: css({ padding: "0px" }),
};
